import type { FC } from "react";
import { memo } from "react";
import { useNavigate } from "react-router-dom";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import PulseLoading from "@/design-system/pulse-loading";
import TelemetryButton from "@/design-system/telemetry-button";
import LocationIcon from "@/src/common/icons/LocationIcon";
import ImageLoader, { type IImageLoader } from "@Components/ImageLoader";
import { type TelemetryClickFromType } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import {
    ComposableSpacing,
    type PropsWithStyleX,
    type QueryXsMax,
    colors,
    genericStyles,
    globalLimitStyles,
} from "../../../styles/tokens.stylex";

export interface IShoppingMallTile extends PropsWithStyleX {
    id: string;
    key?: number | string;
    title?: string;
    mallLogo: string;
    mallImageLoader: IImageLoader;
    imageBackgroundColor?: string;
    navigateMallUrl?: string;
    mapLink: string;
    mapAnchor: string;
    mapLinkText: string;
    onClick?: (_event: React.MouseEvent<HTMLAnchorElement>, _tileId: string) => boolean | void;
    origin?: TelemetryClickFromType;
}

const ShoppingMallTile: FC<IShoppingMallTile> = memo((props: IShoppingMallTile) => {
    const navigate = useNavigate();

    const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (props.onClick) {
            props.onClick(event, props.id);
        }
    };

    const renderDiscountImage = () => (
        <div {...stylex.props(styles.discountImageContainer)}>
            <div {...stylex.props(styles.discountImage)}>
                <ImageLoader
                    {...props.mallImageLoader}
                    style={{ backgroundColor: props.imageBackgroundColor }}
                    srcSetAxis="width"
                    size={600}
                    lazyLoading={true}
                />
            </div>
        </div>
    );

    const renderBrandImage = () => (
        <div {...stylex.props(styles.brandImageContainer)}>
            <div {...stylex.props(styles.bandImageBorder(props.imageBackgroundColor || "#fff"))}>
                <div {...stylex.props(styles.brandImageBorder)}>
                    <ImageLoader
                        lazyLoading
                        src={props.mallLogo || ""}
                        alt={props.mallLogo}
                        srcSetAxis="width"
                        size={300}
                    />
                </div>
            </div>
        </div>
    );

    const renderDiscountDetails = () => (
        <div {...stylex.props(styles.discountDetails)}>
            <div {...stylex.props(styles.discountDescription)}>
                {props.title && (
                    <div {...stylex.props(styles.discountTitle)}>
                        <Typography
                            fontStyle="BodyXL"
                            styleX={[styles.title, globalLimitStyles.TextTwoLinesCap]}
                        >
                            {props.title}
                        </Typography>
                    </div>
                )}
                <div {...stylex.props(styles.additionalInfo)}>
                    <Button
                        as="button"
                        size="None"
                        variant="transparent"
                        styleX={styles.mapLink}
                        onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();

                            const navUrl = props.mapLink + (props.mapAnchor ? `#${props.mapAnchor}` : "");

                            navigate(navUrl);
                        }}
                    >
                        <span {...stylex.props(styles.mapIcon)}>
                            <LocationIcon />
                        </span>
                        {props.mapLinkText}
                    </Button>
                </div>
            </div>
        </div>
    );

    const renderContent = () => (
        <>
            <TelemetryButton
                as="a"
                target="_self"
                variant="none"
                size="None"
                id={props.id}
                title={props.title}
                href={props.navigateMallUrl}
                eventName={
                    props.origin && props.origin !== "Undefined" && props.origin !== "SearchResult"
                        ? "common-discountClick"
                        : null
                }
                itemId={props.id}
                onClick={onClick}
            >
                {renderDiscountImage()}
                {renderDiscountDetails()}
            </TelemetryButton>
            {renderBrandImage()}
        </>
    );

    const renderLoadingEffect = () => (
        <>
            <PulseLoading stylexItem={styles.discountImageContainer} />
            <div {...stylex.props(styles.discountDetails)}>
                <div {...stylex.props(styles.discountDescription)}>
                    <PulseLoading stylexItem={styles.discountTitle} pulseNumber={2} />
                </div>
                <div {...stylex.props(styles.brandImageContainer)}>
                    <div {...stylex.props(styles.brandImageBorder)}>
                        <PulseLoading />
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div {...stylex.props(styles.container, props.styleX)}>
            {props.id ? renderContent() : renderLoadingEffect()}
        </div>
    );
});

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const styles = stylex.create({
    container: {
        display: "block",
        position: "relative",
        height: "100%",
        width: "100%",
    },

    discountImageContainer: {
        borderRadius: 8,
        overflow: "hidden",
        alignItems: "center",
        boxShadow: "inset 0 0 10px 0 rgba(0,0,0,0.2)",
        display: "flex",
        position: "relative",
        paddingTop: "50%",
        width: "100%",
    },

    discountImage: {
        alignItems: "center",
        display: "flex",
        position: "absolute",
        height: "100%",
        top: 0,
        width: "100%",
    },

    brandImageContainer: {
        position: "absolute",
        paddingTop: { default: "calc(50% - 40px)", [xsMax]: "calc(50%  - calc(25vw - 10px)/2)" },
        right: 20,
        top: 0,
    },

    bandImageBorder: (color: string) => ({
        borderColor: color,
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: 6,
    }),

    brandImageBorder: {
        padding: { default: 12, [xsMax]: "15%" },
        borderRadius: genericStyles.borderRadiusFullRound,
        alignItems: "center",
        backgroundColor: colors.bg_Primary,
        display: "flex",
        justifyContent: "center",
        outlineColor: "#b4b4b4",
        outlineStyle: "solid",
        outlineWidth: 1,
        height: { default: 80, [xsMax]: "calc(25vw - 10px)" }, //"calc(25vw - 10px)" },
        width: { default: 80, [xsMax]: "calc(25vw - 10px)" },
    },

    discountDetails: {
        position: "relative",
    },

    discountDescription: {
        borderRadius: 8,
        textAlign: "left",
        paddingTop: ComposableSpacing.s3,
    },
    discountTitle: {
        width: { default: "calc(100% - 100px)", [xsMax]: "calc(100% - (25vw + 15px))" },
    },

    title: {
        fontWeight: 700,
        height: "3rem",
        marginBottom: { default: ComposableSpacing.s2, [xsMax]: ComposableSpacing.s1 },
    },

    additionalInfo: {
        gap: 20,
        alignItems: "center",
        display: "flex",
    },
    mapLink: {
        gap: ComposableSpacing.s2,
        color: colors.fg_BrandBlue,
        display: "flex",
    },

    mapIcon: {
        color: colors.fg_BrandMagenta,
        height: 22,
        width: 22,
    },
});

export default ShoppingMallTile;
